import * as Dialog from "@radix-ui/react-dialog";
import { AlertTriangle, RefreshCw, X } from "lucide-react";

/* ---------- Modal (Radix: ESC, focus-trap e ARIA de fábrica) ----------
 *
 * FOLHA no celular, DIÁLOGO no desktop.
 *
 * Era um diálogo centrado nos dois. Num telefone isso dá uma caixa flutuando no
 * meio da tela, presa a `-translate-y-1/2`, com o conteúdo inteiro — cabeçalho
 * junto — rolando dentro dela. Três consequências que apareceram no CRM:
 *
 *   1. O TÍTULO SUMIA ao rolar, e com ele o X de fechar. Num formulário de
 *      quatro abas, a pessoa rolava até o fim e não tinha mais como sair.
 *   2. `overflow-y-auto` LIGA O EIXO X junto no navegador. Bastou a fila de
 *      abas medir mais que a caixa para o formulário inteiro deslizar para o
 *      lado — foi exatamente o que a captura mostrou, com os rótulos cortados
 *      na margem esquerda.
 *   3. `vh` NÃO É A TELA no celular: a barra do navegador entra e sai, e
 *      `90vh` estoura a área visível. Daí `dvh`, que acompanha.
 *
 * Agora o corpo é a ÚNICA parte que rola, só na vertical. Cabeçalho e rodapé
 * ficam presos, e a folha nasce colada embaixo — onde o polegar alcança.
 *
 * `footer` é opcional e existe para a ação principal não depender de rolagem:
 * "Salvar" no fim de um formulário longo é o botão mais importante da tela e
 * era o mais difícil de achar. Sem a prop, nada muda para quem já usa o Modal.
 */
export function Modal({ open, onClose, title, children, footer, maxWidth = "max-w-md", testId }) {
  return (
    <Dialog.Root open={open} onOpenChange={(v) => !v && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-slate-900/50" />
        <Dialog.Content
          data-testid={testId}
          className={`fixed z-50 flex flex-col bg-white shadow-xl focus:outline-none
            inset-x-0 bottom-0 mx-auto max-h-[92dvh] rounded-t-2xl ${maxWidth}
            sm:inset-x-auto sm:bottom-auto sm:left-1/2 sm:top-1/2 sm:w-[calc(100vw-2rem)]
            sm:-translate-x-1/2 sm:-translate-y-1/2 sm:max-h-[90vh]
            sm:rounded-xl sm:border sm:border-slate-200`}
        >
          {/* Alça: diz "isto é uma folha" antes de qualquer texto ser lido. */}
          <div className="sm:hidden shrink-0 flex justify-center pt-2.5 pb-1" aria-hidden>
            <span className="h-1 w-10 rounded-full bg-slate-300" />
          </div>

          <div className="shrink-0 flex items-start justify-between gap-3 px-5 sm:px-6 pt-3 sm:pt-6 pb-3">
            <Dialog.Title className="font-heading font-semibold text-slate-900 leading-snug min-w-0">{title}</Dialog.Title>
            {/* 44px de alvo no toque (WCAG 2.5.5) e o tamanho antigo no mouse. */}
            <Dialog.Close
              className="shrink-0 -mr-2 -mt-1 inline-flex items-center justify-center h-11 w-11 sm:h-8 sm:w-8 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100"
              aria-label="Fechar"
            >
              <X size={18} />
            </Dialog.Close>
          </div>

          {/* `overflow-x-hidden` é o freio do item 2 acima: o que for largo
              demais resolve a própria rolagem (ver a fila de abas do CRM), em
              vez de arrastar o formulário junto. */}
          <div className="flex-1 overflow-y-auto overflow-x-hidden scrollbar-thin px-5 sm:px-6 pb-5 sm:pb-6">
            {children}
          </div>

          {footer && (
            /* `env(safe-area-inset-bottom)` afasta o botão da barra de gestos do
               iPhone — sem isso ele fica embaixo dela e o toque não chega. */
            <div className="shrink-0 border-t border-slate-100 bg-white px-5 sm:px-6 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] rounded-b-none sm:rounded-b-xl">
              {footer}
            </div>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/* ---------- Confirmação para ações destrutivas ---------- */
export function ConfirmDialog({ open, onClose, onConfirm, title, message, confirmLabel = "Confirmar", danger = true }) {
  return (
    <Modal open={open} onClose={onClose} title={title} testId="confirm-dialog">
      <p className="text-sm text-slate-600 leading-relaxed">{message}</p>
      {/* `flex-col-reverse` no celular NÃO é capricho de layout: a ordem do DOM
          é [Voltar, Confirmar] para o teclado e o leitor de tela lerem cancelar
          primeiro, e o reverse desenha o destrutivo EM CIMA — deixando "Voltar"
          na base, que é onde o polegar cai. Empilhado na ordem natural, o botão
          de excluir ficaria exatamente sob o dedo. No desktop volta a ser uma
          linha, com o destrutivo à direita. */}
      <div className="mt-5 flex flex-col-reverse gap-2.5 sm:flex-row sm:gap-3 sm:justify-end">
        <button onClick={onClose} data-testid="confirm-cancel"
          className="h-11 sm:h-9 px-4 rounded-lg text-sm font-medium text-slate-600 border border-slate-200 hover:bg-slate-50">Voltar</button>
        <button onClick={() => { onConfirm(); onClose(); }} data-testid="confirm-ok" autoFocus
          className={`h-11 sm:h-9 px-4 rounded-lg text-sm font-semibold text-white ${danger ? "bg-red-600 hover:bg-red-700" : "bg-brand-600 hover:bg-brand-700"}`}>
          {confirmLabel}
        </button>
      </div>
    </Modal>
  );
}

/* ---------- KPI ---------- */
export function KpiCard({ icon: Icon, label, value, cls = "text-slate-900", delta, testId }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid={testId}>
      <div className="flex items-center gap-2 text-slate-500 text-xs font-semibold tracking-wider uppercase mb-2">
        {Icon && <Icon size={15} className="text-brand-600" />}{label}
      </div>
      <p className={`font-heading text-2xl font-semibold ${cls}`}>
        {value}
        {delta != null && (
          <span className={`ml-2 text-xs font-semibold align-middle ${delta >= 0 ? "text-emerald-600" : "text-red-500"}`}>
            {delta >= 0 ? "↑" : "↓"} {Math.abs(delta).toFixed(0)}%
          </span>
        )}
      </p>
    </div>
  );
}

/* ---------- Status badge (cores unificadas dos estados de pedido) ---------- */
export const ORDER_STATUS_CLS = {
  pendente: "bg-slate-100 text-slate-600",
  pago: "bg-blue-50 text-blue-700",
  separacao: "bg-amber-50 text-amber-700",
  enviado: "bg-violet-50 text-violet-700",
  entregue: "bg-emerald-50 text-emerald-700",
  cancelado: "bg-red-50 text-red-600",
};

export function StatusBadge({ value, map = ORDER_STATUS_CLS }) {
  return (
    <span className={`text-xs font-semibold capitalize px-2 py-0.5 rounded-full ${map[value] || "bg-slate-100 text-slate-600"}`}>
      {String(value).replace("_", " ")}
    </span>
  );
}

/* ---------- Estados de tela ---------- */
export function EmptyState({ icon: Icon, title, desc, action, testId }) {
  return (
    <div className="py-12 px-6 text-center" data-testid={testId}>
      {Icon && <Icon size={32} className="mx-auto text-slate-300 mb-3" />}
      <p className="font-medium text-slate-700">{title}</p>
      {desc && <p className="text-sm text-slate-400 mt-1 max-w-sm mx-auto">{desc}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function ErrorState({ onRetry, message = "Não foi possível carregar os dados." }) {
  return (
    <div className="py-12 px-6 text-center" data-testid="error-state">
      <AlertTriangle size={30} className="mx-auto text-amber-500 mb-3" />
      <p className="font-medium text-slate-700">{message}</p>
      <p className="text-sm text-slate-400 mt-1">Verifique sua conexão ou tente novamente.</p>
      <button onClick={onRetry} data-testid="error-retry-btn"
        className="mt-4 inline-flex items-center gap-2 bg-brand-600 hover:bg-brand-700 text-white text-sm font-semibold px-4 py-2 rounded-lg">
        <RefreshCw size={14} /> Tentar novamente
      </button>
    </div>
  );
}

export function TableSkeleton({ rows = 5, cols = 5 }) {
  return (
    <div className="p-5 space-y-3 animate-pulse" data-testid="table-skeleton">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="flex gap-4">
          {Array.from({ length: cols }).map((_, j) => (
            <div key={j} className={`h-4 rounded bg-slate-100 ${j === 0 ? "w-1/4" : "flex-1"}`} />
          ))}
        </div>
      ))}
    </div>
  );
}

export function KpiSkeleton({ count = 4 }) {
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 animate-pulse" data-testid="kpi-skeleton">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="bg-white border border-slate-200 rounded-xl p-5 space-y-3">
          <div className="h-3 w-2/3 rounded bg-slate-100" />
          <div className="h-7 w-1/2 rounded bg-slate-100" />
        </div>
      ))}
    </div>
  );
}

/* ---------- Barra de progresso (metas) ---------- */
export function ProgressBar({ pct, testId }) {
  const clamped = Math.min(100, Math.max(0, pct || 0));
  const color = clamped >= 100 ? "bg-emerald-500" : clamped >= 60 ? "bg-brand-500" : "bg-amber-400";
  return (
    <div className="flex items-center gap-2" data-testid={testId}>
      <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
        <div className={`h-full rounded-full ${color} transition-all`} style={{ width: `${clamped}%` }} />
      </div>
      <span className="text-xs font-mono font-semibold text-slate-600 w-12 text-right">{(pct || 0).toFixed(0)}%</span>
    </div>
  );
}
